// src/utils/orderTypeUtils.js
// Utility functions for order type conversions between UI and API formats

/**
 * Convert UI order type format to API format
 * @param {string} uiOrderType - Order type from UI (e.g., 'dine-in', 'takeaway', 'delivery')
 * @returns {string} - Order type for API (e.g., 'dine_in', 'take_away', 'delivery')
 */
export const convertUIToAPIOrderType = (uiOrderType) => {
  switch (uiOrderType) {
    case 'dine-in':
      return 'dine_in';
    case 'takeaway':
      return 'take_away';
    case 'delivery':
      return 'delivery';
    default:
      return uiOrderType.replace('-', '_'); // fallback for any other hyphenated types
  }
};

/**
 * Convert API order type format to UI format
 * @param {string} apiOrderType - Order type from API (e.g., 'dine_in', 'take_away', 'delivery')
 * @returns {string} - Order type for UI (e.g., 'dine-in', 'takeaway', 'delivery')
 */
export const convertAPIToUIOrderType = (apiOrderType) => {
  switch (apiOrderType) {
    case 'dine_in':
      return 'dine-in';
    case 'take_away':
      return 'takeaway';
    case 'delivery':
      return 'delivery';
    default:
      return apiOrderType.replace('_', '-'); // fallback for any other underscore types
  }
};

/**
 * Get display-friendly order type (for messages, labels, etc.)
 * @param {string} orderType - Order type in either UI or API format
 * @returns {string} - Display-friendly order type
 */
export const getDisplayOrderType = (orderType) => {
  // Normalize to UI format first
  const uiFormat = orderType.includes('_') ? convertAPIToUIOrderType(orderType) : orderType;
  
  switch (uiFormat) {
    case 'dine-in':
      return 'Dine-in';
    case 'takeaway':
      return 'Takeaway';
    case 'delivery':
      return 'Delivery';
    default:
      return orderType.charAt(0).toUpperCase() + orderType.slice(1).replace(/[-_]/g, ' ');
  }
};

/**
 * Get order type options for UI components
 * @returns {Array} - Array of order type options
 */
export const getOrderTypeOptions = () => [
  { value: 'dine-in', label: 'Dine-in' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'delivery', label: 'Delivery' }
];
